import { useState, useEffect } from 'react';
import { climaAPI, zonasAPI } from '../api/client';
import AsistenteLLM from '../components/AsistenteLLM';
import { useTranslation } from 'react-i18next';
import { renderSug as renderSugShared } from '../utils/sugerencias';

const WMO_EMOJI = (code) => {
  if (code === 0) return '☀️';
  if ([1, 2].includes(code)) return '⛅';
  if (code === 3) return '☁️';
  if ([45, 48].includes(code)) return '🌫️';
  if ([51,53,55,61,63,65,80,81,82].includes(code)) return '🌧️';
  if ([71,73,75].includes(code)) return '❄️';
  if ([95,96,99].includes(code)) return '⛈️';
  return '🌡️';
};

const PRIORIDAD_COLOR = { alta: '#C0392B', warning: '#C97820', info: '#4A7C59' };
const PRIORIDAD_BG = { alta: '#FDECEA', warning: '#FDF0DC', info: '#E8F5EC' };

export default function Clima() {
  const { t, i18n } = useTranslation();
  const [zonas, setZonas] = useState([]);
  const [zonaId, setZonaId] = useState(null);
  const [clima, setClima] = useState(null);
  const [sugerencias, setSugerencias] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [zonasStatus, setZonasStatus] = useState('loading'); // 'loading' | 'ready' | 'error'

  const cargarZonas = () => {
    setZonasStatus('loading');
    zonasAPI.list().then(r => {
      const conGPS = r.data.filter(z => z.lat != null && z.lng != null);
      setZonas(conGPS);
      setZonasStatus('ready');
      if (conGPS.length > 0) cargar(conGPS[0].id);
    }).catch(e => {
      console.error('[Clima] Error cargando zonas:', e.response?.data || e.message || e);
      setZonasStatus('error');
    });
  };

  useEffect(() => { cargarZonas(); }, []); // eslint-disable-line

  const cargar = async (id) => {
    setZonaId(id); setLoading(true); setError('');
    try {
      const [c, s] = await Promise.all([climaAPI.get(id), climaAPI.sugerencias(id)]);
      setClima(c.data);
      setSugerencias(s.data.sugerencias || []);
    } catch (e) {
      console.error('[Clima] Error:', e.response?.data || e.message || e);
      setError(e.response?.data?.detail || t('clima.errorMsg'));
    } finally { setLoading(false); }
  };

  const lang = i18n.language?.slice(0, 2) || 'es';

  const renderSug = (sug) => renderSugShared(sug, t, lang);

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{t('clima.title')}</h1>
          <p style={S.sub}>{t('clima.subtitle')}</p>
        </div>
        {zonas.length > 1 && (
          <select className="form-input" style={{ width: 'auto', minWidth: 160 }}
            value={zonaId || ''} onChange={e => cargar(parseInt(e.target.value))}>
            {zonas.map(z => <option key={z.id} value={z.id}>{z.nombre}</option>)}
          </select>
        )}
      </div>

      {zonasStatus === 'loading' ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>
          {t('clima.loading')}
        </div>
      ) : zonasStatus === 'error' ? (
        <div className="empty-state">
          <div className="icon">⚠️</div>
          <h3>{t('clima.zonasError')}</h3>
          <button className="btn btn-primary" onClick={cargarZonas} style={{ marginTop: 12 }}>
            {t('clima.retry')}
          </button>
        </div>
      ) : zonas.length === 0 ? (
        <div className="empty-state">
          <div className="icon">📍</div>
          <h3>{t('clima.noGPS')}</h3>
          <p>{t('clima.noGPSMsg')}</p>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: '#8B6355' }}>
          {t('clima.loading')}
        </div>
      ) : error ? (
        <div style={{ background: '#FDECEA', borderRadius: 12, padding: 20, color: '#C0392B' }}>
          {error}
        </div>
      ) : clima && (
        <>
          {clima.alertas?.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 20 }}>
              {clima.alertas.map((a, i) => {
                const dia = a.params?.dia_delta === 0 ? t('clima.today')
                  : a.params?.dia_delta === 1 ? t('clima.tomorrow')
                  : a.params?.date
                    ? new Date(a.params.date + 'T12:00:00').toLocaleDateString(lang, { weekday: 'long' })
                    : t('clima.soon');
                return (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', borderRadius: 12,
                    background: a.prioridad === 'alta' ? '#FDECEA' : '#FDF0DC',
                    border: `1px solid ${a.prioridad === 'alta' ? '#C0392B' : '#C97820'}`,
                  }}>
                    <span style={{ fontSize: 22 }}>{a.icono}</span>
                    <div style={{ fontSize: 14, color: '#2C1A0E', fontWeight: 600 }}>
                      {t('clima.alertas.' + a.clave, { ...a.params, dia })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={S.climaCard}>
            <div style={S.climaMain}>
              <div style={{ fontSize: 64 }}>{WMO_EMOJI(clima.weathercode)}</div>
              <div>
                <div style={{ fontSize: 48, fontWeight: 700, fontFamily: "'DM Sans', sans-serif", lineHeight: 1 }}>
                  {clima.temp?.toFixed(0)}°C
                </div>
                <div style={{ fontSize: 16, color: '#8B6355', marginTop: 4 }}>
                  {t('weatherCodes.' + clima.weathercode, { defaultValue: clima.descripcion })}
                </div>
              </div>
            </div>
            <div className="clima-stats">
              <StatItem icon="🌡️" label={t('clima.maxMin')} value={`${clima.temp_max?.toFixed(0)}° / ${clima.temp_min?.toFixed(0)}°`} />
              <StatItem icon="🌧️" label={t('clima.precipLabel')} value={`${clima.rain_mm?.toFixed(1)} mm`} />
              <StatItem icon="💨" label={t('clima.windLabel')} value={`${clima.wind_kmh?.toFixed(0)} km/h`} />
              <StatItem icon="🕐" label={t('clima.updated')} value={new Date(clima.updated_at).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })} />
            </div>
          </div>

          {clima.hourly_today?.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <h2 style={S.sectionTitle}>{t('clima.hourlyTitle')}</h2>
              {clima.mejor_franja && (
                <div style={{ fontSize: 13, color: '#4A7C59', marginBottom: 10, fontWeight: 600 }}>
                  ✅ {t('clima.bestWindow', { start: clima.mejor_franja.start, end: clima.mejor_franja.end })}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 6 }}>
                {clima.hourly_today.map(h => {
                  const enFranja = clima.mejor_franja && h.hour >= clima.mejor_franja.start && h.hour < clima.mejor_franja.end;
                  return (
                    <div key={h.hour} style={{
                      minWidth: 56, textAlign: 'center', padding: '10px 4px', borderRadius: 10,
                      background: enFranja ? '#E8F5EC' : 'white',
                      border: enFranja ? '1px solid #4A7C59' : '1px solid rgba(232,146,42,0.12)',
                    }}>
                      <div style={{ fontSize: 11, color: '#8B6355' }}>{h.hour}h</div>
                      <div style={{ fontSize: 18 }}>{WMO_EMOJI(h.weathercode)}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 2 }}>{h.temp?.toFixed(0)}°</div>
                      <div style={{ fontSize: 11, color: h.rain_prob > 40 ? '#5B8DD9' : '#B8A99C' }}>💧{h.rain_prob ?? 0}%</div>
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          {clima.forecast_7d?.length > 0 && (
            <section style={{ marginBottom: 28 }}>
              <h2 style={S.sectionTitle}>{t('clima.forecastTitle')}</h2>
              <div className="grid-forecast">
                {clima.forecast_7d.map((d, i) => {
                  const fecha = new Date(d.date + 'T12:00:00');
                  const label = i === 0 ? t('clima.today') : i === 1 ? t('clima.tomorrow') :
                    fecha.toLocaleDateString(lang, { weekday: 'short' });
                  return (
                    <div key={d.date} style={{ ...S.diaCard, ...(i === 0 ? S.diaCardHoy : {}) }}>
                      <div style={{ fontSize: 12, color: '#8B6355', marginBottom: 4 }}>{label}</div>
                      <div style={{ fontSize: 24 }}>{WMO_EMOJI(d.weathercode)}</div>
                      <div style={{ fontSize: 10, color: '#8B6355', marginTop: 2, lineHeight: 1.2, overflowWrap: 'anywhere', hyphens: 'auto' }}>{t('weatherCodes.' + d.weathercode, { defaultValue: d.descripcion || '' })}</div>
                      <div style={{ fontSize: 14, fontWeight: 600, marginTop: 4 }}>{d.temp_max?.toFixed(0)}°</div>
                      <div style={{ fontSize: 12, color: '#8B6355' }}>{d.temp_min?.toFixed(0)}°</div>
                      {d.rain_prob != null && <div style={{ fontSize: 11, color: d.rain_prob > 40 ? '#5B8DD9' : '#B8A99C', marginTop: 2 }}>💧{d.rain_prob}%</div>}
                      {d.rain_mm > 1 && <div style={{ fontSize: 11, color: '#5B8DD9' }}>{d.rain_mm?.toFixed(0)}mm</div>}
                    </div>
                  );
                })}
              </div>
            </section>
          )}

          <section>
            <h2 style={S.sectionTitle}>{t('clima.suggestionsTitle')}</h2>
            {sugerencias.length === 0 ? (
              <div style={{ color: '#8B6355', fontSize: 14, padding: '12px 0' }}>{t('clima.noSuggestions')}</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {sugerencias.map((sug, i) => {
                  const { msg, det } = renderSug(sug);
                  return (
                    <div key={i} style={{
                      ...S.sugerenciaCard,
                      borderLeft: `4px solid ${PRIORIDAD_COLOR[sug.prioridad] || '#8B6355'}`,
                      background: PRIORIDAD_BG[sug.prioridad] || 'white',
                    }}>
                      <span style={{ fontSize: 20 }}>{sug.icono}</span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 14, color: '#2C1A0E' }}>{msg}</div>
                        {det && <div style={{ fontSize: 12, color: '#8B6355', marginTop: 4, lineHeight: 1.5 }}>{det}</div>}
                        {sug.colmena_id && <div style={{ fontSize: 12, color: '#8B6355', marginTop: 2 }}>{t('clima.specificHive')}</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </section>

          <AsistenteLLM zonaId={zonaId} />
        </>
      )}
    </div>
  );
}

function StatItem({ icon, label, value }) {
  return (
    <div style={{ textAlign: 'center', padding: '10px 16px' }}>
      <div style={{ fontSize: 20 }}>{icon}</div>
      <div style={{ fontSize: 18, fontWeight: 600, margin: '4px 0' }}>{value}</div>
      <div style={{ fontSize: 11, color: '#8B6355' }}>{label}</div>
    </div>
  );
}

const S = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 26, fontFamily: "'DM Sans', sans-serif" },
  sub: { color: '#8B6355', fontSize: 14, marginTop: 4 },
  climaCard: { background: 'white', borderRadius: 16, padding: 28, marginBottom: 24, boxShadow: '0 2px 12px rgba(44,26,14,0.08)', border: '1px solid rgba(232,146,42,0.12)' },
  climaMain: { display: 'flex', alignItems: 'center', gap: 20, marginBottom: 20 },
  sectionTitle: { fontSize: 17, fontWeight: 600, marginBottom: 14, fontFamily: "'DM Sans', sans-serif" },
  diaCard: { background: 'white', borderRadius: 12, padding: '12px 8px', textAlign: 'center', border: '1px solid rgba(232,146,42,0.12)', boxShadow: '0 1px 4px rgba(44,26,14,0.04)' },
  diaCardHoy: { border: '2px solid #E8922A', background: '#FFFBF2' },
  sugerenciaCard: { display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', borderRadius: 12, boxShadow: '0 1px 4px rgba(44,26,14,0.04)' },
};
